/**
 * Map item (HEX-73) — a buyable shop wares that shares cross-character world
 * knowledge.
 *
 * Buying it reveals, to THIS character, every tile within `MAP_REVEAL_RADIUS`
 * of the shop that ANY traveller has discovered (`tile.metadata.discoveredBy
 * !== null`) but that this character cannot yet see. The revealed tiles render
 * in the inherited-memory face — REVEAL-NOT-CREDIT: no global first-discovery,
 * no `discoveries` leaderboard tick, no entry in this character's personal
 * `explored` set. It shares what others found; it does not fake your discovery.
 *
 * This module is the pure core — the reveal-set computation + the offer shape.
 * The world-side wiring (materialising tiles, repainting, persisting the
 * `Character.revealed` set) lives in the game session (`App.ts`), which holds
 * the renderer + world store the shop flow has no handle on.
 */

import { type Hex, hexKey } from '../core/hex';

/** Gold price of a map at the shop. Tunable (HEX-73 DoD: ~100g). */
export const MAP_ITEM_PRICE = 100;

/**
 * Axial radius around the shop a bought map reveals. Wider than the vision disk
 * (radius 2) so 100g buys a meaningful patch of inherited frontier, not a
 * sliver. Tunable.
 */
export const MAP_REVEAL_RADIUS = 6;

/**
 * Lookups a map reveal needs, injected by the game session so this module stays
 * pure + unit-testable without a world store or renderer.
 */
export interface MapRevealQuery {
  /** True when ANY character has discovered the tile at `hex` (`discoveredBy !== null`). */
  discoveredByAnyone(hex: Hex): boolean;
  /**
   * True when `key` is already visible/known to the buying character — currently
   * in vision, previously rendered into their memory, personally explored, or
   * already revealed by an earlier map. Such tiles are not "hidden to you" and
   * are excluded from both the reveal and the pre-purchase count.
   */
  knownToCharacter(key: string): boolean;
}

/**
 * The `q,r` keys a map bought at `centre` would newly reveal: every hex within
 * `radius` that someone has discovered and the buyer cannot already see. Pure +
 * deterministic — the same inputs always yield the same keys, so the
 * pre-purchase tooltip count is exactly what a purchase reveals.
 */
export function revealableHexes(centre: Hex, radius: number, query: MapRevealQuery): string[] {
  const out: string[] = [];
  for (let dq = -radius; dq <= radius; dq++) {
    const drMin = Math.max(-radius, -dq - radius);
    const drMax = Math.min(radius, -dq + radius);
    for (let dr = drMin; dr <= drMax; dr++) {
      const hex = { q: centre.q + dq, r: centre.r + dr };
      const key = hexKey(hex);
      if (query.knownToCharacter(key)) continue;
      if (!query.discoveredByAnyone(hex)) continue;
      out.push(key);
    }
  }
  return out;
}

/**
 * A map purchase offered in the shop. Built by the game session (which knows the
 * world + renderer) and handed to `ShopFlow`, keeping the flow a dumb renderer
 * for it.
 */
export interface MapOffer {
  /** Gold cost. */
  readonly price: number;
  /**
   * Count of currently-hidden-to-you-but-discovered-by-someone tiles in radius —
   * what the purchase will reveal. Drives the hover tooltip; `0` greys the buy
   * (100g never buys a 1-tile map).
   */
  readonly revealCount: number;
  /**
   * Execute the purchase: deduct gold, fold the reveal set into the character's
   * persisted `revealed`, render the newly-revealed tiles, and persist. A no-op
   * when the character can't afford it (the flow gates this too).
   */
  buy(): void | Promise<void>;
}
